import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useNotification, useTranslation } from "@refinedev/core";

import { useTheme } from "@mui/material/styles";

import { TitrationMedByClass } from "@/constants/titration";
import { useGdmtEnabled } from "@/hooks/useGdmtEnabled";
import { usePatientMedStatements } from "@/hooks/usePatientMedStatements";
import { TitrationPlanEntry } from "@/routes/dashboard/components/patient-summary-pages/titration-plan";
import {
  entryToMedicationInput,
  medicationRequestToDiscontinuedInput,
} from "@/routes/dashboard/components/patient-summary-pages/titration-plan/titration-plan-mappers";
import { DATE_FORMAT_ISO, formatDateInUserTimezone } from "@/utils";
import {
  CREATE_TITRATION_CARE_PLAN_MUTATION,
  CarePlanStatus,
  CreateTitrationCarePlanData,
  CreateTitrationCarePlanInput,
  GET_TITRATION_CARE_PLAN_QUERY,
  GdmtMedicationClass,
  type GetTitrationCarePlanInput,
  type GetTitrationCarePlanResponse,
  MedicationRequestStatus,
  type TitrationCarePlan,
  TitrationMedicationInput,
  TitrationProgram,
} from "@/utils/GraphQLClient/titration";
import { formatters } from "@/utils/fhir-utils";
import { hasUnreferencedContainedMedication } from "@/utils/medication-ingredients";
import {
  categorizeMedRequests,
  categorizeMedStatements,
  createDefaultMedRequestMap,
  createDefaultMedStatementMap,
  createEmptyStateMap,
  emptyByClass,
  emptyDosageFields,
  isCarePlanStatus,
  medicationRequestToEntry,
  seedMedicationCategories,
} from "@/utils/titration";

import * as Sentry from "@sentry/react";
import { fhirR4 } from "@smile-cdr/fhirts";
import dayjs from "dayjs";
import { CombinedError, useMutation, useQuery } from "urql";

export interface TitrationPlanContextValue {
  enabled: boolean;
  /* FHIR patient ID (no 'Patient/' prefix) */
  fhirPatientId?: string;
  /* FHIR CarePlan with the titration category */
  carePlan?: TitrationCarePlan["carePlan"];
  /* The titration plan's MedicationRequests (up to 4 — one per drug class) */
  medicationRequestMap: Map<GdmtMedicationClass, fhirR4.MedicationRequest | undefined>;
  /* The titration plan's MedicationStatements (up to 4 - one per drug class) */
  medicationStatementMap: Map<GdmtMedicationClass, fhirR4.MedicationStatement | undefined>;
  /* All MedicationStatements returned by the plan:
   ** - Up to 8 (2 from the same class) can be returned if all are discontinues & replaced
   ** - Used to override the fetched medications list
   */
  titrationMedicationStatements: fhirR4.MedicationStatement[];
  /* All active existing titration MedicationStatement resources of a patient */
  patientMedicationsByClass: Record<GdmtMedicationClass, fhirR4.MedicationStatement[]>;
  /* Working form state, one entry per drug class. Seeded from the fetched plan and edited via `updateEntry`. */
  titrationPlanStateMap: Record<GdmtMedicationClass, TitrationPlanEntry>;
  /* Is there a query/mutation being performed? */
  isLoading: boolean;
  /* Is the pane open? (defaulted to false) */
  isOpen: boolean;
  /* Is the titration plan being edited? */
  isEditing: boolean;
  /* Opens the pane */
  open: () => void;
  /* Closes the pane */
  close: () => void;
  /* Resets the context - used when a user exists a patient's drilldown */
  reset: () => void;
  /* Resets the context, while preserving fhirPatientId - used when ending titration plan */
  endPlan: () => void;
  /* Sets the FHIR patient ID for the context */
  setFhirPatientId: (id?: string) => void;
  /* Sets the careplan state to editing */
  handleStartEditing: () => void;
  /* Sets the careplan state to read only */
  handleStopEditing: () => void;
  /* Reset MedicationRequest forms when changes are discarded */
  handleDiscardChanges: () => void;
  /* Creates an active plan - replaces existing active plan, if it exists*/
  createTitrationPlan: (args: {
    patientId: string;
    practitionerId: string;
    startDate: string;
  }) => Promise<CombinedError | undefined>;
  /* Updates care plan with new fields */
  updateCarePlan: (plan: TitrationCarePlan["carePlan"]) => void;
  /* Merges a patch into a single drug class's entry, triggering a re-render. */
  updateEntry: (medClass: GdmtMedicationClass, patch: Partial<TitrationPlanEntry>) => void;
  /* Undoes an in-progress medication replacement, restoring the discontinued med's form values */
  handleStopReplacingMed: (medClass: GdmtMedicationClass) => void;
  /* Enabled edit mode for one medication request card */
  startMedRequestEdit: (medClass: GdmtMedicationClass) => void;
  /* Enables view mode for one medication request card */
  completeMedRequestEntry: (medClass: GdmtMedicationClass) => void;
  /* Collapses or expands one medication request card */
  toggleMedRequestCollapse: (medClass: GdmtMedicationClass) => void;
}

export const TitrationPlanContext = createContext<TitrationPlanContextValue>({
  enabled: false,
  isOpen: false,
  isLoading: false,
  isEditing: false,
  fhirPatientId: undefined,
  carePlan: undefined,
  medicationRequestMap: new Map(),
  medicationStatementMap: new Map(),
  titrationMedicationStatements: [],
  patientMedicationsByClass: emptyByClass(),
  titrationPlanStateMap: createEmptyStateMap(),
  open: () => {},
  close: () => {},
  reset: () => {},
  endPlan: () => {},
  setFhirPatientId: () => {},
  handleStartEditing: () => {},
  handleStopEditing: () => {},
  handleDiscardChanges: () => {},
  createTitrationPlan: async () => undefined,
  updateCarePlan: () => {},
  updateEntry: () => {},
  handleStopReplacingMed: () => {},
  startMedRequestEdit: () => {},
  completeMedRequestEntry: () => {},
  toggleMedRequestCollapse: () => {},
});

export const TitrationPlanProvider = ({ children }: { children: React.ReactNode }) => {
  const { translate } = useTranslation();
  const { open: openNotification } = useNotification();

  // ============================================================================================================
  // STATES
  // ============================================================================================================
  const [fhirPatientId, setFhirPatientId] = useState<string | undefined>(undefined);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // The patient's featuresv2 carries titration:UseGdmtTitrate when the org's licence has
  // titration (VMD-794): the backend derives it from the org's Account_Setting for every user.
  const { enabled: isTitrationEnabled, loading: isTitrationEnabledLoading } = useGdmtEnabled(fhirPatientId ?? "");

  // ============================================================================================================
  // FHIR resources that are a part of titration
  // ============================================================================================================
  const [planState, setPlanState] = useState<{
    carePlan: TitrationCarePlan["carePlan"] | undefined;
    medRequestMap: TitrationPlanContextValue["medicationRequestMap"];
    medStatementMap: TitrationPlanContextValue["medicationStatementMap"];
    medStatements: TitrationPlanContextValue["titrationMedicationStatements"];
  }>({
    carePlan: undefined,
    medRequestMap: createDefaultMedRequestMap(),
    medStatementMap: createDefaultMedStatementMap(),
    medStatements: [],
  });
  // Med request form values
  // NOTE: Each entry will always be populated with empty fields VS medReqMap can have undefined values
  const [medReqFormState, setMedReqFormState] =
    useState<TitrationPlanContextValue["titrationPlanStateMap"]>(createEmptyStateMap());
  // Patient's existing, active MedicationStatement resources categorized by MedReq class
  const [activeMedStatements, setActiveMedStatements] = useState<TitrationMedByClass>(emptyByClass());

  /**************************** HOOKS ***************************/
  // The Medications tab fetches its own copy through the same hook; this one only feeds the pane.
  const { isLoading: patientMedicationsByClassLoading, titrationMedsByClass } = usePatientMedStatements(
    fhirPatientId ?? "",
    { enabled: isTitrationEnabled }
  );

  // ============================================================================================================
  // QUERIES/MUTATIONS
  // ============================================================================================================

  const queryPatientId = fhirPatientId ?? "";
  const [result] = useQuery<GetTitrationCarePlanResponse, { input: GetTitrationCarePlanInput }>({
    query: GET_TITRATION_CARE_PLAN_QUERY,
    variables: { input: { patient_id: queryPatientId } },
    requestPolicy: "network-only",
    pause: !fhirPatientId || isTitrationEnabledLoading || !isTitrationEnabled || Boolean(planState.carePlan), // Prevent refetch when creating/activating plan
  });
  const [{ fetching: isCreatingPlan }, createTitrationCarePlan] = useMutation<
    CreateTitrationCarePlanData,
    { input: CreateTitrationCarePlanInput }
  >(CREATE_TITRATION_CARE_PLAN_MUTATION);

  const titrationCarePlan = result.data?.getTitrationCarePlan;
  // urql keeps the previous `data` and `operation` across pause toggles and variable changes until
  // the new fetch delivers, so a retained plan can belong to a previous patient.
  const fetchedPlanPatientId = result.operation?.variables.input.patient_id;
  /* Stable key: re-seed only when fetched content changes, not on every refetch's fresh
     object ref — otherwise a background refetch would clobber in-progress form edits. */
  const fetchedPlanKey = JSON.stringify(titrationCarePlan ?? null);
  // Seed only a settled result for the current patient while the gate is on. The gate matters
  // because a paused query keeps its result, so drop-then-restore must re-seed unchanged content;
  // the ownership check because that retained result may be a previous patient's; `!fetching`
  // because seeding during the un-pausing render would set `carePlan`, re-pause the query and
  // dispose the in-flight fetch. Patient and fetching are deps so a delivered result seeds even
  // when its content matches. Edits are safe: the query is paused whenever a plan is loaded.
  useEffect(() => {
    if (titrationCarePlan && !result.fetching && isTitrationEnabled && fetchedPlanPatientId === queryPatientId) {
      const formattedMedReqs = categorizeMedRequests(titrationCarePlan.medicationRequests);
      const medStatementMap = categorizeMedStatements(titrationCarePlan.medicationStatements);
      setPlanState(() => ({
        carePlan: titrationCarePlan.carePlan,
        medRequestMap: formattedMedReqs.originalState,
        medStatementMap,
        medStatements: titrationCarePlan.medicationStatements ?? [],
      }));
      // Carry each class's RASi sub-class forward from its source MedicationStatement: a
      // subsequent round hides the sub-class dropdown but must still send it in the payload.
      setMedReqFormState(seedMedicationCategories(formattedMedReqs.formState, medStatementMap));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedPlanKey, result.fetching, isTitrationEnabled, queryPatientId]);

  /* Re-seed only when the number of meds in any class changes, not on every fresh object ref from a background refetch. */
  const titrationMedsCountKey = Object.values(titrationMedsByClass)
    .map(meds => meds.length)
    .join("-");
  useEffect(() => {
    setActiveMedStatements(titrationMedsByClass);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titrationMedsCountKey]);

  // Nothing can open the pane without the feature; the surfaces that call open() are already
  // hidden, this closes the entry point by construction.
  const handleOpenPane = useCallback(() => {
    if (isTitrationEnabled) {
      setIsOpen(true);
    }
  }, [isTitrationEnabled]);
  const handleClosePane = () => setIsOpen(false);
  const resetState = useCallback(
    ({
      preservePatient = false,
      preserveMedStatements = false,
    }: { preservePatient?: boolean; preserveMedStatements?: boolean } = {}) => {
      setIsOpen(false);
      setIsEditing(false);
      if (!preservePatient) {
        setFhirPatientId(undefined);
        setActiveMedStatements(emptyByClass());
      }
      setPlanState({
        carePlan: undefined,
        medRequestMap: createDefaultMedRequestMap(),
        // When titration is ended, preserve the MedStatements so it displays in the meds list
        medStatementMap: preserveMedStatements ? planState.medStatementMap : createDefaultMedStatementMap(),
        medStatements: preserveMedStatements ? planState.medStatements : [],
      });
      setMedReqFormState(createEmptyStateMap());
    },
    [planState.medStatementMap, planState.medStatements]
  );

  const handleReset = useCallback(() => resetState(), [resetState]);
  /* Used when titration is ended - similar to handleReset, but the fhirPatientId is preserved */
  const handleEndTitration = useCallback(
    () => resetState({ preservePatient: true, preserveMedStatements: true }),
    [resetState]
  );

  // VMD-794: when titration turns off after mount (cache invalidation, or a features query error,
  // which reads as off), close the pane, leave edit mode and drop the loaded plan. The patient id
  // stays because the drill-down is still open; PatientSummaryTabs' unmount owns the full reset.
  // resetState is not a dep: its identity changes on every plan load and only the flag edge matters.
  useEffect(() => {
    if (!isTitrationEnabled) {
      resetState({ preservePatient: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTitrationEnabled]);

  const handleStartEditing = useCallback(() => {
    setIsEditing(true);
    // Entering edit mode starts a fresh titration round that replaces the active plan, so each
    // class begins from a blank decision. Clear the decision-dependent fields seeded from the
    // loaded plan (decision, reasons, note, new dose, in-progress replacement) while preserving
    // the medication and current/target dose context the card still displays.
    setMedReqFormState(prev => {
      const next = {} as TitrationPlanContextValue["titrationPlanStateMap"];
      (Object.keys(prev) as GdmtMedicationClass[]).forEach(medClass => {
        next[medClass] = {
          ...prev[medClass],
          decision: undefined,
          decisionReasons: [],
          reason: "",
          newDosage: emptyDosageFields,
          isReplacingMedication: false,
          replacedMedication: undefined,
        };
      });
      return next;
    });
  }, []);

  const handleStopEditing = () => setIsEditing(false);

  // Discard changes in edit mode: Stop editing and reset the form state while preserving original values
  const handleDiscardChanges = useCallback(() => {
    handleStopEditing();

    const medRequests = Array.from(planState.medRequestMap.values()).filter(
      (medRequest): medRequest is fhirR4.MedicationRequest => medRequest !== undefined
    );
    const formattedMedReqs = categorizeMedRequests(medRequests);
    // Re-seed the RASi sub-class from the source statements so discarding an edit keeps it
    // in the payload, matching the load path.
    setMedReqFormState(seedMedicationCategories(formattedMedReqs.formState, planState.medStatementMap));
  }, [planState.medRequestMap, planState.medStatementMap]);

  const updateCarePlan = useCallback((plan: TitrationCarePlan["carePlan"]) => {
    setPlanState(prev => ({ ...prev, carePlan: plan }));
  }, []);

  /**************************** UPDATE MED REQ FORM ***************************/

  const updateEntry = useCallback((medClass: GdmtMedicationClass, patch: Partial<TitrationPlanEntry>) => {
    setMedReqFormState(prev => {
      const previousEntry = prev[medClass];
      const nextEntry = { ...previousEntry, ...patch };

      // The initial-plan card edits `currentDosage`, but readiness (`isEntryReadyForPlan`)
      // and the save mapper (`entryToDosageStep`) read `newDosage`. Mirror the current
      // dose value/unit into `newDosage` so the new plan's dose defaults to the entered
      // current dose. Frequency is owned by the frequency Select (writes `newDosage.frequency`).
      if (patch.currentDosage !== undefined) {
        nextEntry.newDosage = {
          ...nextEntry.newDosage,
          value: patch.currentDosage.value,
          unit: patch.currentDosage.unit,
        };
      }
      return { ...prev, [medClass]: nextEntry };
    });
  }, []);

  const handleStopReplacingMed = useCallback(
    (medClass: GdmtMedicationClass) => {
      const { decision, decision_reasons, reason_for_titration, source_medication_statement_id } =
        medReqFormState[medClass].replacedMedication ?? {};
      const discontinuedMedReq = planState.medRequestMap.get(medClass);
      // Undo the replacement: drop `replacedMedication` and relocate all of its fields back onto
      // the entry, restoring the original (discontinued) medication request's form values.
      updateEntry(medClass, {
        ...medicationRequestToEntry(discontinuedMedReq),
        medicationId: source_medication_statement_id ?? "",
        decision,
        decisionReasons: decision_reasons ?? [],
        reason: reason_for_titration ?? "",
        isReplacingMedication: false,
        replacedMedication: undefined,
      });
    },
    [medReqFormState, planState.medRequestMap, updateEntry]
  );

  const startMedRequestEdit = useCallback(
    (medClass: GdmtMedicationClass) => {
      // Always expand when entering edit mode so the form is visible.
      updateEntry(medClass, { status: "editing", collapsed: false });
    },
    [updateEntry]
  );

  const completeMedRequestEntry = useCallback(
    (medClass: GdmtMedicationClass) => {
      // Auto-collapse on completion so the user can scan the remaining classes.
      updateEntry(medClass, { status: "complete", collapsed: true });
    },
    [updateEntry]
  );

  const toggleMedRequestCollapse = useCallback(
    (medClass: GdmtMedicationClass) => {
      const entry = medReqFormState[medClass];
      // Collapsing keeps the card in "editing" even when nothing was entered, so it stays a
      // visible (incomplete) collapsed card. The footer suppresses its error while it's both
      // collapsed and empty, so an untouched card is incomplete without gating the save.
      updateEntry(medClass, { collapsed: !entry.collapsed });
    },
    [medReqFormState, updateEntry]
  );

  /**************************** CREATE/ACTIVATE/END TITRATION PLANS ***************************/
  /**
   * Create an active titration plan (sets care plan and med request statuses to 'active')
   * @param input FHIR Patient & Practitioner ID (uuid only) - checks done in titration-plan-pane.tsx
   */
  const createTitrationPlan = useCallback(
    async ({
      patientId,
      practitionerId,
      startDate,
    }: {
      patientId: string;
      practitionerId: string;
      startDate: string;
    }) => {
      const medicationsInput = (Object.entries(medReqFormState) as [GdmtMedicationClass, TitrationPlanEntry][]).reduce<
        TitrationMedicationInput[]
      >((acc, [drugClassCode, medReqEntry]) => {
        if (medReqEntry.status !== "complete") {
          return acc;
        }
        const input = entryToMedicationInput(drugClassCode, medReqEntry);
        const discontinuedMedRequest = planState.medRequestMap.get(drugClassCode);

        /**
         * - Editing the same med: Get the MedStat ID from planState
         * - Incoming replacement med:
         *      - If med exists, it should be retrieved from input.source_medication_statement_id.
         *      - If med doesn't exist, a new med is being created and `existingPlanStatementId` is undefined
         */
        const existingPlanStatementId = medReqEntry.isReplacingMedication
          ? undefined
          : planState.medStatementMap.get(drugClassCode)?.id;

        acc.push({
          ...input,
          source_medication_statement_id: input.source_medication_statement_id ?? existingPlanStatementId ?? undefined,
          // The BE requires the specific medication class. For RASi this is the
          // acei/arbs/arnis sub-category the practitioner picks (the add-to-plan
          // button is gated on it); the other classes leave it undefined.
          medication_statement_class: medReqEntry.medicationCategory,
          // When the class's medication is being replaced, capture the discontinued
          // (patient's current) medication so the BE records what was swapped out.
          ...(medReqEntry.isReplacingMedication &&
            discontinuedMedRequest && {
              discontinued_medication: medicationRequestToDiscontinuedInput(
                drugClassCode,
                discontinuedMedRequest,
                medReqEntry.replacedMedication
              ),
            }),
        });
        return acc;
      }, []);

      // The backend rebuilds a source statement's `contained` from the plan, so a statement
      // whose contained Medication its reference does not name would lose that Medication's
      // strengths and codings. Refuse, the same way the medication form does.
      const knownStatements = [...planState.medStatements, ...Object.values(activeMedStatements).flat()];
      const unreferencedIds = medicationsInput.flatMap(({ source_medication_statement_id: id }) => {
        const statement = id ? knownStatements.find(candidate => candidate.id === id) : undefined;
        return id && statement && hasUnreferencedContainedMedication(statement) ? [id] : [];
      });
      if (unreferencedIds.length) {
        // Support needs the ids to correct the records; nothing was written.
        Sentry.captureMessage("TitrationPlan: source statement has an unreferenced contained Medication", {
          level: "warning",
          tags: { component: "TitrationPlanContext", feature: "titration" },
          extra: { medicationStatementIds: unreferencedIds },
        });
        return new CombinedError({
          graphQLErrors: [translate("pages.medications.gdmt.errors.unreferencedMedication")],
        });
      }

      const input: CreateTitrationCarePlanInput = {
        patient_id: formatters.formatPatient(patientId),
        practitioner_id: formatters.formatPractitioner(practitionerId),
        program: TitrationProgram.HeartFailureGdmt,
        start_date: startDate,
        medications: medicationsInput,
      };
      const { data, error } = await createTitrationCarePlan({ input });

      if (error) {
        Sentry.captureException("TitrationPlan: Creation failed", {
          level: "error",
          tags: {
            component: "TitrationPlanContext",
            feature: "titration",
            operation: "CREATE_TITRATION_CARE_PLAN_MUTATION",
          },
          extra: { error: JSON.stringify(error), fhirPatientId, carePlanId: planState.carePlan?.id },
        });
        return error;
      }

      const activePlan = data?.createTitrationCarePlan;
      if (activePlan) {
        const formattedMedReqs = categorizeMedRequests(activePlan.medicationRequests);
        const medStatementMap = categorizeMedStatements(activePlan.medicationStatements);
        setPlanState(prev => ({
          ...prev,
          carePlan: activePlan.carePlan,
          medRequestMap: formattedMedReqs.originalState,
          medStatementMap,
          medStatements: activePlan.medicationStatements ?? [],
        }));
        // Keep the sub-class on the form so re-editing the just-saved plan still submits it.
        setMedReqFormState(seedMedicationCategories(formattedMedReqs.formState, medStatementMap));
        openNotification?.({
          message: translate("pages.medications.gdmt.saveSuccess"),
          type: "success",
        });
        handleStopEditing();
      }
    },
    [
      activeMedStatements,
      createTitrationCarePlan,
      fhirPatientId,
      medReqFormState,
      openNotification,
      planState.carePlan?.id,
      planState.medRequestMap,
      planState.medStatementMap,
      planState.medStatements,
      translate,
    ]
  );

  const value = useMemo(() => {
    return {
      enabled: isTitrationEnabled,
      fhirPatientId,
      isLoading: result.fetching || isCreatingPlan || patientMedicationsByClassLoading || isTitrationEnabledLoading,
      isOpen,
      isEditing,
      carePlan: planState.carePlan,
      medicationRequestMap: planState.medRequestMap,
      medicationStatementMap: planState.medStatementMap,
      titrationMedicationStatements: planState.medStatements,
      titrationPlanStateMap: medReqFormState,
      patientMedicationsByClass: activeMedStatements,
      updateEntry,
      handleStopReplacingMed,
      close: handleClosePane,
      open: handleOpenPane,
      reset: handleReset,
      endPlan: handleEndTitration,
      setFhirPatientId,
      updateCarePlan,
      handleStartEditing,
      handleStopEditing,
      createTitrationPlan,
      handleDiscardChanges,
      startMedRequestEdit,
      completeMedRequestEntry,
      toggleMedRequestCollapse,
    };
  }, [
    isTitrationEnabled,
    fhirPatientId,
    result.fetching,
    isCreatingPlan,
    patientMedicationsByClassLoading,
    isTitrationEnabledLoading,
    isOpen,
    isEditing,
    planState.carePlan,
    planState.medRequestMap,
    planState.medStatementMap,
    planState.medStatements,
    medReqFormState,
    activeMedStatements,
    updateEntry,
    handleStopReplacingMed,
    handleOpenPane,
    handleReset,
    handleEndTitration,
    updateCarePlan,
    handleStartEditing,
    createTitrationPlan,
    handleDiscardChanges,
    startMedRequestEdit,
    completeMedRequestEntry,
    toggleMedRequestCollapse,
  ]);

  return <TitrationPlanContext.Provider value={value}>{children}</TitrationPlanContext.Provider>;
};

type MedRequestColourPalette = Record<string, { backgroundColor: string; color: string }>;

type UseTitrationPlanResult = TitrationPlanContextValue & {
  status?: CarePlanStatus;
  utcStartDate?: string;
  timezonedStartDate?: string;
  medRequestPalette: MedRequestColourPalette;
};

export const useTitrationPlan = (): UseTitrationPlanResult => {
  const context = useContext(TitrationPlanContext);
  const { palette } = useTheme();

  if (context === undefined) {
    throw new Error(`useTitrationPlan must be used within a TitrationPlanProvider`);
  }

  const MED_REQ_COLOUR_PALETTE: MedRequestColourPalette = {
    [MedicationRequestStatus.draft]: {
      backgroundColor: palette.orange.light,
      color: palette.orange.main,
    },
    [MedicationRequestStatus.active]: {
      backgroundColor: palette.alertGreen.main,
      color: palette.success.dark,
    },
    "on-hold": {
      backgroundColor: palette.alertYellow.main,
      color: palette.alertYellow.dark,
    },
    [MedicationRequestStatus.onHold]: {
      backgroundColor: palette.alertYellow.main,
      color: palette.alertYellow.dark,
    },
    [MedicationRequestStatus.stopped]: {
      backgroundColor: palette.error.main,
      color: palette.error.dark,
    },
    [MedicationRequestStatus.unknown]: {
      backgroundColor: palette.background.blueGrey,
      color: palette.primary.main,
    },
  };

  const carePlan = context.carePlan;
  const carePlanStatus = carePlan?.status;
  const carePlanStartDate = carePlan?.period?.start;

  return {
    ...context,
    status: carePlanStatus && isCarePlanStatus(carePlanStatus) ? carePlanStatus : undefined,
    utcStartDate: carePlanStartDate ? dayjs(carePlanStartDate).format(DATE_FORMAT_ISO) : undefined,
    timezonedStartDate: carePlanStartDate ? formatDateInUserTimezone(carePlanStartDate, DATE_FORMAT_ISO) : undefined,
    medRequestPalette: MED_REQ_COLOUR_PALETTE,
  };
};
